'use client';

/** A/B TESTS — same data as classic /funnels/ab-tests: stats['ab-tests'].tests per funnel. */
import { useEffect, useState } from 'react';
import type { BusinessProject } from '@/lib/business-projects';
import { Glass, StatusChip, EmptyState, mono, display } from '@/future/primitives';

type Variant = { variantId: number; variantKey: string; variantName: string; isControl: boolean; assignments: number; conversions: number; conversionRate: number };
type Test = { id: number; testName: string; pagePath: string; status: string; description: string; totalAssignments: number; winnerVariantId: number | null; variants: Variant[] };

export function AbTestsSurface({ project }: { project: BusinessProject | null }) {
  const [tests, setTests] = useState<Test[] | null>(null);
  const fid = project?.funnelId;

  useEffect(() => {
    if (!fid) return;
    let live = true;
    const load = () =>
      fetch(`/api/funnels/${fid}/stats`).then(r => r.json())
        .then(d => { if (live) setTests(d?.stats?.['ab-tests']?.tests ?? []); })
        .catch(() => { if (live) setTests([]); });
    load();
    const t = setInterval(load, 60_000);
    return () => { live = false; clearInterval(t); };
  }, [fid]);

  if (!fid) return <EmptyState what="NO FUNNEL WIRED" action="This project has no funnel — A/B tests live on funnels." />;
  if (tests === null) return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING EXPERIMENTS…</div>;
  if (!tests.length) return <EmptyState what="NO EXPERIMENTS" action="No A/B tests created for this funnel yet." />;

  return (
    <div className="mx-auto max-w-5xl flex flex-col gap-4">
      {tests.map(t => (
        <Glass key={t.id} className="p-5">
          <div className="flex items-center gap-3">
            <div style={{ ...display(30), color: 'var(--uif-t1)' }}>{t.testName}</div>
            <StatusChip label={t.status.toUpperCase()} kind={t.status === 'active' ? 'good' : t.status === 'completed' ? 'dim' : 'warn'} />
            <div style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{t.pagePath}</div>
            <div className="ml-auto" style={{ ...mono(16, '.2em'), color: 'var(--uif-t4)' }}>{t.totalAssignments} ASSIGNMENTS</div>
          </div>
          {t.description && <div className="mt-2" style={{ ...mono(18), color: 'var(--uif-t4)', letterSpacing: '.04em' }}>{t.description}</div>}
          <div className="mt-4 flex flex-col gap-2">
            {t.variants.map(v => {
              const best = Math.max(...t.variants.map(x => x.conversionRate), 0.0001);
              const isWinner = t.winnerVariantId === v.variantId;
              return (
                <div key={v.variantId} className="flex items-center gap-3">
                  <div className="w-6 text-center" style={{ ...mono(20), color: isWinner ? 'var(--uif-good)' : 'var(--uif-t3)' }}>{v.variantKey}</div>
                  <div className="w-44 truncate" style={{ ...mono(18), color: 'var(--uif-t3)' }}>
                    {v.variantName}{v.isControl && <span style={{ color: 'var(--uif-t5)' }}> · CTRL</span>}
                  </div>
                  <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ background: 'rgba(255,255,255,.04)' }}>
                    <div className="h-full rounded-sm" style={{
                      width: `${Math.max(2, v.conversionRate / best * 100)}%`,
                      background: isWinner ? 'linear-gradient(90deg, rgba(126,231,196,.25), rgba(126,231,196,.6))' : 'linear-gradient(90deg, rgba(126,205,231,.18), rgba(126,205,231,.45))',
                      transition: 'width .4s ease',
                    }} />
                  </div>
                  <div className="w-24 text-right" style={{ ...mono(16), color: 'var(--uif-t4)' }}>{v.assignments} → {v.conversions}</div>
                  <div className="w-14 text-right" style={{ ...mono(18), color: isWinner ? 'var(--uif-good)' : 'var(--uif-t2)' }}>{v.conversionRate}%</div>
                </div>
              );
            })}
          </div>
        </Glass>
      ))}
    </div>
  );
}
